import { Button, IconButton, useMediaQuery, useTheme, type ButtonProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, type To } from 'react-router-dom';
import type { SystemStyleObject } from '@mui/system';

export interface ListActionButtonProps extends Omit<ButtonProps, 'sx'> {
  customSx?: SystemStyleObject<Theme>;
  to?: To;
}

const baseStyles: SystemStyleObject<Theme> = {
  textTransform: 'none',
  boxShadow: 'none',
};

const variantStyles = (variant: ButtonProps['variant'], theme: Theme): SystemStyleObject<Theme> => {
  if (variant === 'contained') {
    return {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    };
  }

  return {
    color: theme.palette.primary.main,
    borderColor: alpha(theme.palette.primary.main, 0.6),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  };
};

export default function ListActionButton({
  customSx,
  variant = 'outlined',
  to,
  component,
  startIcon,
  endIcon,
  children,
  ...rest
}: ListActionButtonProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const combinedSx: SystemStyleObject<Theme> = {
    ...baseStyles,
    ...variantStyles(variant, theme),
    ...customSx,
  };
  const routerComponent = component ?? (to ? RouterLink : undefined);
  const icon = startIcon ?? endIcon;
  const linkProps = {
    ...(routerComponent ? { component: routerComponent } : {}),
    ...(to ? { to } : {}),
  };

  return (
    isMobile ? (
      <IconButton
        size="small"
        sx={combinedSx}
        {...linkProps}
        {...rest}
      >
        {icon ?? children}
      </IconButton>
    ) : (
    <Button
      variant={variant}
      size="small"
      sx={combinedSx}
      startIcon={startIcon}
      endIcon={endIcon}
      {...linkProps}
      {...rest}
    >
      {children}
    </Button>
    )
  );
}
